import React, { useEffect, useState, useMemo, useCallback } from "react";
import { MenuItem, Icon, Separator } from "@showitinc/showit-wc";
import cx from "classnames";
import { useFeatureFlagEnabled } from "posthog-js/react";
import ContextMenuWrapper from "./ContextMenuWrapper";
import ClipboardStore from "../../stores/ClipboardStore";
import DesignViewStore from "../../stores/DesignViewStore";
import ModelFactory from "../../models/ModelFactory";
import DocumentStore from "../../document/DocumentStore";
import { COMMANDS } from "../../document/commands";
import DesignBrowserActions from "../../actions/DesignChooserActions";
import AppConstants from "../../constants/AppConstants";
import uiPrefix from "../../utils/UiPrefixUtils";
import EventUtils from "../../utils/EventUtils";
import UIActions from "../../actions/UIActions";
import UserActions from "../../actions/UserActions";
import styles from "./PagePanelHeaderContextMenu.module.css";

const {
  ITEM_TYPE_BLOCK,
  CONTEXT_ITEM_BUTTON,
  STAGE_VIEW_MOBILE,
  STAGE_VIEW_SPLIT,
} = AppConstants;

const PagePanelHeaderContextMenu = ({ canvasSets, blockContainer, pages, siteBlocks, active }) => {
  const [pasteLabel, setPasteLabel] = useState("");
  const [pasteEnabled, setPasteEnabled] = useState(false);
  const isOptimizeLayerOrderEnabled = useFeatureFlagEnabled("optimize-layer-order");

  useEffect(() => {
    const checkClipboard = () => {
      let newPasteLabel = "";
      let newPasteEnabled = false;
      if (ClipboardStore.canPasteBlock()) {
        newPasteEnabled = true;
        newPasteLabel = ClipboardStore.getContentLabel();
      }

      setPasteEnabled(newPasteEnabled);
      setPasteLabel(newPasteLabel);
    };

    const unsubscribeStore = ClipboardStore.listen(checkClipboard);

    checkClipboard();

    return () => {
      if (unsubscribeStore) {
        unsubscribeStore();
      }
    };
  }, []);

  const _paste = () => {
    DocumentStore.dispatch({ type: COMMANDS.PASTE, payload: { type: ITEM_TYPE_BLOCK } });
  };

  const _addCanvas = () => {
    DocumentStore.dispatch({
      type: COMMANDS.ADD_BLOCK,
      payload: { block: ModelFactory.getBlock(), applyTheme: true },
    });
  };

  const _openCanvasChooser = () => {
    DesignBrowserActions.openDialog({ title: "Choose A Canvas" });
  };

  const _onCanvasSetChange = useCallback(
    (canvasSet) => {
      const containerId = blockContainer.get("isCanvasSet") ? blockContainer.get("id") : null;
      if (containerId !== canvasSet.get("id")) {
        UIActions.selectCanvasSet({ canvasSet });
      }
    },
    [blockContainer]
  );

  const _makeCanvasSetDefault = useCallback((canvasSet) => {
    const id = canvasSet.get("id");
    const slug = canvasSet.get("slug");

    DocumentStore.dispatch({ type: COMMANDS.MAKE_CANVAS_SET_DEFAULT, payload: { id } });
    UserActions.trackEvent({
      event: "canvas-set-default",
      meta: { canvasSet: slug },
    });
  }, []);

  const _getCanvasSetList = useCallback(() => {
    const isCanvasSet = blockContainer.get("isCanvasSet");
    const currentCanvasSet = blockContainer.get("currentCanvasSet");
    const isDefaultCanvasSet =
      isCanvasSet && blockContainer.getIn(["currentCanvasSet", "default"]) === 1;

    const canvasSetList = canvasSets.map((canvasSet) => (
      <MenuItem
        data-e2e={uiPrefix(`switch-canvas-set-${CONTEXT_ITEM_BUTTON}`)}
        onClick={(e) => {
          e.stopPropagation();
          _onCanvasSetChange(canvasSet);
        }}
        key={`set-${canvasSet.get("id")}`}
      >
        {canvasSet.get("name")}
      </MenuItem>
    ));

    return canvasSetList ? (
      <div>
        {isCanvasSet && !isDefaultCanvasSet && (
          <>
            <MenuItem
              data-e2e={uiPrefix(`make-default-canvas-set-${CONTEXT_ITEM_BUTTON}`)}
              key="make-default-canvas-set"
              onClick={() => _makeCanvasSetDefault(currentCanvasSet)}
            >
              Make default canvas set
            </MenuItem>
            <Separator variant="context-menu" key={"canvas-set-separator"}/>
          </>
        )}
        <div className={styles.submenu}>
          <Icon type="canvasSet" size="20" className={styles.icon} />
          <div>Site Canvas Sets</div>
        </div>
        <div className={styles["menu-item"]}>{canvasSetList}</div>
        <Separator variant="context-menu" />
      </div>
    ) : null;
  }, [blockContainer, canvasSets, _onCanvasSetChange, _makeCanvasSetDefault]);

  const _onPageChange = useCallback(
    (page) => {
      const containerId = blockContainer.get("isPage") ? blockContainer.get("id") : null;
      if (containerId !== page.get("id")) {
        if (page !== null) {
          UIActions.selectPage({ page });
        }
      }
    },
    [blockContainer]
  );

  const _getPageList = useCallback(() => {
    const pagesList = [];
    const wpList = [];

    if (pages && pages.size > 0) {
      pages.forEach((page) => {
        const pageId = page.get("id");

        const list = page.has("wp") ? wpList : pagesList;
        list.push(
          <MenuItem
            data-e2e={uiPrefix(`switch-page-${CONTEXT_ITEM_BUTTON}`)}
            onClick={() => _onPageChange(page)}
            key={`pg-${pageId}`}
          >
            {page.get("name")}
          </MenuItem>
        );
      });
    }

    return (
      <div>
        <div className={styles.submenu}>
          <Icon type="sitePage" size="20" className={styles.icon} />
          <div>Pages</div>
        </div>
        <div className={styles["menu-item"]}>{pagesList}</div>
        <Separator variant="context-menu" />
        <div className={styles.submenu}>
          <Icon type="wordpress" size="20" className={styles.icon} />
          <div>Blog Templates</div>
        </div>
        <div className={styles["menu-item"]}>{wpList}</div>
        <Separator variant="context-menu" />
      </div>
    );
  }, [pages, _onPageChange]);

  const _onSiteBlockChange = useCallback(
    (block) => (e) => {
      e.stopPropagation();
      const containerId = blockContainer.get("isBlock") ? blockContainer.get("id") : null;
      if (containerId !== block.get("id")) {
        if (block !== null) {
          UIActions.selectSiteBlock({ block });
          EventUtils.emitPassthroughClick();
        }
      }
    },
    [blockContainer]
  );

  const _getSiteBlockList = useCallback(() => {
    if (siteBlocks.size === 0) {
      return null;
    }

    const options = [];

    siteBlocks.forEach((block) => {
      const blockId = block.get("id");
      options.push(
        <MenuItem
          data-e2e={uiPrefix(`switch-site-canvas-${CONTEXT_ITEM_BUTTON}`)}
          onClick={_onSiteBlockChange(block)}
          key={`sb-${blockId}`}
        >
          {block.get("name")}
        </MenuItem>
      );
    });

    return (
      <div>
        <div className={styles.submenu}>
          <Icon type="siteCanvas" size="20" className={styles.icon} />
          <div>Site Canvases</div>
        </div>
        <div className={styles["menu-item"]}>{options}</div>
      </div>
    );
  }, [siteBlocks, _onSiteBlockChange]);

  const canvasSetList = useMemo(() => _getCanvasSetList(), [_getCanvasSetList]);
  const pageList = useMemo(() => _getPageList(), [_getPageList]);
  const siteBlockList = useMemo(() => _getSiteBlockList(), [_getSiteBlockList]);
  const isPage = useMemo(() => blockContainer.get("isPage"), [blockContainer]);
  const pageId = useMemo(
    () => (blockContainer.get("isPage") ? blockContainer.get("id") : null),
    [blockContainer]
  );

  const options = useMemo(() => {
    const _getPageMenuOptions = () => {
      const statePasteLabel = pasteLabel || "Canvas";
      let pasteAction = null;
      let pasteClasses = { "drop-down-button": true, disabled: true, separator: true };
      let menuPasteLabel = "Paste Canvas";

      if (pasteEnabled) {
        pasteAction = _paste;
        pasteClasses.disabled = false;
        menuPasteLabel = `Paste ${statePasteLabel}`;
      }

      pasteClasses = cx(pasteClasses);

      const items = [
        <MenuItem
          key="canvas-chooser"
          data-e2e={uiPrefix(`add-from-library-${CONTEXT_ITEM_BUTTON}`)}
          onClick={_openCanvasChooser}
        >
          Add from Library
        </MenuItem>,
        <MenuItem
          key="blank-canvas"
          data-e2e={uiPrefix(`add-blank-canvas-${CONTEXT_ITEM_BUTTON}`)}
          onClick={_addCanvas}
        >
          Add Blank Canvas
        </MenuItem>,
        <MenuItem
          key="paste"
          data-e2e={uiPrefix(`paste-canvas-${CONTEXT_ITEM_BUTTON}`)}
          onClick={pasteAction}
          disabled={!pasteEnabled}
          className={!pasteEnabled ? styles.disabled : ""}
        >
          {menuPasteLabel}
        </MenuItem>,
      ];

      if (isOptimizeLayerOrderEnabled) {
        items.push(
          <Separator variant="context-menu" key={"options-separator"}/>,
          <MenuItem
            key="optimize-layer-order"
            data-testid="optimize-layer-order-page-menu"
            onClick={() => {
              const stageView = DesignViewStore.getStageView();
              const layout = stageView === STAGE_VIEW_SPLIT ? STAGE_VIEW_MOBILE : stageView;
              UserActions.trackPostHogEvent("layer_order_optimize_clicked", {
                scope: "page",
                layout,
              });
              DocumentStore.dispatch({
                type: COMMANDS.SORT_PAGE_BY_READING_ORDER,
                payload: { pageId, layout },
              });
            }}
          >
            Optimize layer order (beta)
          </MenuItem>
        );
      }

      items.push(<Separator variant="context-menu" key={"reading-order-separator"}/>);

      return items;
    };

    if (isPage) {
      return _getPageMenuOptions();
    }
    return [];
  }, [isPage, pasteEnabled, pasteLabel, pageId, isOptimizeLayerOrderEnabled]);

  return (
    <ContextMenuWrapper icon="menuMoreHoriz" active={active}>
      {options}
      <div className={cx("webkit-scrollbar", styles.scrollbar)}>
        {canvasSetList}
        {pageList}
        {siteBlockList}
      </div>
    </ContextMenuWrapper>
  );
};

export default PagePanelHeaderContextMenu;
